import { Divider, FormControl, FormGroup, Grid, TextField, Typography } from "@mui/material";
import * as React from "react";
import HESPFormLabel from "../../HESPFormLabel/HESPFormLabel";
import RegionSelector from "../../Regions/RegionSelector";
import { ChannelSdiArgs, CreateChannelInput } from "./CreateChannelForm";
import { GetOrganizationChannelSettingsQuery_organization } from "../../../views/App/__generated__/GetOrganizationChannelSettingsQuery";
import AuthService from "../../../auth/AuthService";
import SwitchWithTitle from "../../SwitchWithTitle/SwitchWithTitle";
import ChannelIngestSettingsSelectorItem from "../Details/ChannelIngestSettingsSelectorItem";
import styled from "styled-components";
import DatacenterSelector from "../../Regions/DataCenterSelector";
import { IngestProtocol, IngestType } from "../../../__generated__/globalTypes";
import THEOadsAssetKeys, { THEOadsAssetKey } from "../THEOadsAssetKeys";
import _ from "lodash";
import DropdownSelector from "../../Regions/DropdownSelector";
import ChannelSdiManagement, { ChannelSdiAdditionalParams } from "./ChannelSdiManagement";
import { NFL_ORG_ID } from "../../AppSidebar/OrganizationSidebar";

const Selections = styled.div`
    display: flex;
    flex-wrap: wrap;
`;

const SectionTitle = styled.div`
    font-size: 18px;
    font-weight: bold;
`;

const Wrapper = styled.div`
    box-shadow: 0 0px 0px 0 rgb(60 66 87 / 8%), 0 2px 15px 0 rgb(0 0 0 / 12%);
    display: flex;
    padding: 10px;
    border-radius: 8px;
    flex-direction: row;
    max-width: 750px;
    height: 100%;
    align-items: center;
`;

interface Props {
    organization: GetOrganizationChannelSettingsQuery_organization;
    input: CreateChannelInput;
    firstSubmitDone: boolean;
    onNameChange: (val: string) => void;
    onExternalIdChange: (val: string) => void;
    onChangePullUrl: (val: string) => void;
    onLocationChange: (val: string) => void;
    onDatacenterChange: (val: string) => void;
    onChangeIngestProtocol: (protocol: IngestProtocol, type?: IngestType) => void;
    onChangeTLA: () => void;
    onChangeSdi: (sdi: ChannelSdiArgs) => void;
    onChangeTHEOads: (enabled: boolean, assetKeys: THEOadsAssetKey[], podDuration: number) => void;
}

export default function CreateChannelFormBasics ({
    input,
    onNameChange,
    onExternalIdChange,
    onChangePullUrl,
    onLocationChange,
    onDatacenterChange,
    firstSubmitDone,
    organization,
    onChangeTLA,
    onChangeIngestProtocol,
    onChangeSdi,
    onChangeTHEOads,
}: Props) {
    const isTHEOAdmin = AuthService.isTHEOAdmin();

    const sdiInputs =
        input.tla && input.dataCenterId
            ? _.flatMap(
                  organization.tla.datacenters
                      .find((dc) => dc.id === input.dataCenterId)!
                      .tla.deviceContexts.map((ctx) => ctx.sdiInputs)
                      .filter((inp) => inp !== null),
              )
            : [];

    const ndiSources =
        input.tla && input.dataCenterId
            ? _.flatMap(
                  organization.tla.datacenters
                      .find((dc) => dc.id === input.dataCenterId)!
                      .tla.deviceContexts.map((ctx) => ctx.labels ?? []),
              )
                  .filter((label) => label.startsWith("ndi_input."))
                  .map((label) => label.substring(label.indexOf("=") + 1))
            : [];

    const showRtmpPush =
        input.tla &&
        input.dataCenterId &&
        organization.tla.datacenters.find((dc) => dc.id === input.dataCenterId)?.tla.fqdn !== null; // FLIP!!!


    function isValidNdiInput(input: string): boolean {
        // check if the input coming in is a valid IP/port or hostname/port
        // Otherwise don't allow it to be set.
        if (!input || input.trim().length === 0) return false;

        const dummyUrl = `http://${input}`;
        const url = new URL(dummyUrl);
        const port = parseInt(url.port);
        if (port < 1 || port > 65535) {
            return false;
        }

        const hostName = url.hostname;
        const isValidHostname = /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*$/.test(hostName);
        const isValidIp = /^(\d{1,3}\.){3}\d{1,3}$/.test(hostName);

        if (!isValidHostname && !isValidIp) {
            return false;
        }
        return true;
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
                <FormControl fullWidth>
                    <HESPFormLabel label="Name" />
                    <TextField
                        type="text"
                        fullWidth
                        placeholder="Name for your THEOlive channel"
                        variant="outlined"
                        value={input.name}
                        onChange={(e) => onNameChange(e.target.value)}
                    />
                    {}
                    {firstSubmitDone && input.name.trim() === "" && (
                        <Typography variant="subtitle2" color="error">
                            Please enter a name
                        </Typography>
                    )}
                </FormControl>
            </Grid>
            {organization.organizationId === NFL_ORG_ID && (
                <Grid item xs={12} md={7}>
                    <FormControl fullWidth>
                        <HESPFormLabel label="External ID" description="Only available for NFL" />
                        <TextField
                            type="text"
                            fullWidth
                            placeholder="External ID for your channel"
                            variant="outlined"
                            value={input.externalId}
                            onChange={(e) => onExternalIdChange(e.target.value)}
                        />
                    </FormControl>
                </Grid>
            )}
            {organization.tla?.activated === true && isTHEOAdmin && (
                <Grid item xs={12} md={7}>
                    <FormControl>
                        <HESPFormLabel label="THEOlive Anywhere channel" />
                        <SwitchWithTitle
                            checked={input.tla}
                            onChange={() => onChangeTLA()}
                            title="Channel should be deployed on THEOlive Anywhere"
                        />
                    </FormControl>
                </Grid>
            )}
            <Grid item xs={12} md={7}>
                <FormControl fullWidth>
                    <HESPFormLabel label={input.tla ? "Data center" : "Region"} />
                    {input.tla ? (
                        <DatacenterSelector
                            organization={organization}
                            selectDatacenterId={input.dataCenterId!}
                            onChangeSelected={(datacenterId: string) => onDatacenterChange(datacenterId)}
                        />
                    ) : (
                        <RegionSelector
                            selectedRegion={input.ingestLocation!}
                            onChangeSelected={(region: string) => onLocationChange(region)}
                        />
                    )}
                </FormControl>
            </Grid>
            {input.tla && (
                <>
                    <Grid item xs={12} md={7}>
                        <FormControl>
                            <HESPFormLabel label="Ingest Protocol" />
                            <Selections>
                                <ChannelIngestSettingsSelectorItem
                                    onClick={() => onChangeIngestProtocol(IngestProtocol.rtmp)}
                                    selected={
                                        input.ingestProtocol === IngestProtocol.rtmp &&
                                        (input.ingestType === IngestType.pull || !input.ingestType)
                                    }
                                    title={showRtmpPush ? "RTMP Pull" : "RTMP"}
                                    tooltip=""
                                />
                                {showRtmpPush && (
                                    <ChannelIngestSettingsSelectorItem
                                        onClick={() => onChangeIngestProtocol(IngestProtocol.rtmp, IngestType.push)}
                                        selected={
                                            input.ingestProtocol === IngestProtocol.rtmp &&
                                            input.ingestType === IngestType.push
                                        }
                                        title="RTMP Push"
                                        tooltip=""
                                    />
                                )}
                                <ChannelIngestSettingsSelectorItem
                                    onClick={() => onChangeIngestProtocol(IngestProtocol.srt)}
                                    selected={input.ingestProtocol === IngestProtocol.srt}
                                    title="SRT"
                                    tooltip=""
                                />
                                <ChannelIngestSettingsSelectorItem
                                    onClick={() => onChangeIngestProtocol(IngestProtocol.sdi)}
                                    selected={input.ingestProtocol === IngestProtocol.sdi}
                                    title="SDI"
                                    tooltip=""
                                    disabled={sdiInputs.length === 0}
                                />
                                {input.tla && isTHEOAdmin && <ChannelIngestSettingsSelectorItem
                                    onClick={() => onChangeIngestProtocol(IngestProtocol.rtsp)}
                                    selected={
                                        input.ingestProtocol === IngestProtocol.rtsp &&
                                        (input.ingestType === IngestType.pull || !input.ingestType)
                                    }
                                    title='RTSP'
                                    tooltip='This option is only suitable for On-Prem deployments'
                                />}
                                {input.tla && isTHEOAdmin && <ChannelIngestSettingsSelectorItem
                                    onClick={() => onChangeIngestProtocol(IngestProtocol.ndi)}
                                    selected={input.ingestProtocol === IngestProtocol.ndi}
                                    title='NDI'
                                    tooltip='This option is only suitable for On-Prem deployments'
                                />}
                            </Selections>
                        </FormControl>
                    </Grid>
                    {input.tla &&
                        input.ingestProtocol &&
                        input.ingestType !== IngestType.push &&
                        [IngestProtocol.rtmp, IngestProtocol.srt, IngestProtocol.rtsp].includes(input.ingestProtocol) && (
                            <Grid item xs={12} md={7}>
                                <FormControl fullWidth>
                                    <HESPFormLabel label="Pull URL" />
                                    <TextField
                                        type="text"
                                        fullWidth
                                        placeholder="Pull URL"
                                        variant="outlined"
                                        value={input.ingestPullUrl}
                                        onChange={(e) => onChangePullUrl(e.target.value)}
                                    />
                                    {(!input.ingestPullUrl || input.ingestPullUrl.trim().length === 0) &&
                                    input.tla === true ? (
                                        <Typography variant="subtitle2" color="error">
                                            Please enter an ingest Pull URL
                                        </Typography>
                                    ) : (
                                        <></>
                                    )}
                                </FormControl>
                            </Grid>
                        )}
                    {input.tla &&
                        input.ingestProtocol === IngestProtocol.ndi &&
                        input.ingestType !== IngestType.push &&
                        ndiSources.length > 0 && (
                            <Grid item xs={12} md={7}>
                                <FormControl fullWidth>
                                    <HESPFormLabel label="NDI source" />
                                    <DropdownSelector
                                        size="small"
                                        selectedKey={input.ingestPullUrl ?? ""}
                                        onChangeSelected={(url: string) => onChangePullUrl(url)}
                                        items={ndiSources.map((source) => ({
                                            key: `ndi://q?name=${encodeURIComponent(source)}`,
                                            value: source,
                                        }))}
                                    />
                                </FormControl>
                            </Grid>
                        )}
                    {input.tla &&
                        input.ingestProtocol === IngestProtocol.ndi &&
                        input.ingestType !== IngestType.push &&
                        ndiSources.length === 0 && (
                            <Grid item xs={12} md={7}>
                                <FormControl fullWidth>
                                    <HESPFormLabel label="Pull URL" />
                                    <TextField
                                        type="text"
                                        fullWidth
                                        placeholder="ndi://q?name=..."
                                        variant="outlined"
                                        value={input.ingestPullUrl}
                                        onChange={(e) => {
                                            if (!isValidNdiInput(e.target.value)) {
                                                return;
                                            }
                                            onChangePullUrl(e.target.value)
                                        }}
                                    />
                                    {(!input.ingestPullUrl || input.ingestPullUrl.trim().length === 0) &&
                                    input.tla === true ? (
                                        <Typography variant="subtitle2" color="error">
                                            Please enter an ingest Pull URL
                                        </Typography>
                                    ) : (
                                        <></>
                                    )}
                                </FormControl>
                            </Grid>
                        )}
                    {input.tla && input.ingestProtocol && input.ingestProtocol === IngestProtocol.sdi && (
                        <>
                            <Grid item xs={12} md={7}>
                                <FormControl fullWidth>
                                    <HESPFormLabel label="SDI input" />
                                    <DropdownSelector
                                        size="small"
                                        selectedKey={input.sdi?.persistentId ?? ""}
                                        onChangeSelected={(persistentId: string) =>
                                            onChangeSdi({ ...input.sdi!, persistentId })
                                        }
                                        items={sdiInputs.map((sdiInput) => ({
                                            key: sdiInput?.persistentId ?? "",
                                            value: `${sdiInput?.name} (${sdiInput?.persistentId ?? ""})`,
                                        }))}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={7}>
                                <ChannelSdiManagement
                                    onChangeValues={(values: ChannelSdiAdditionalParams) =>
                                        onChangeSdi({
                                            ...input.sdi!,
                                            ...values,
                                        })
                                    }
                                    values={{
                                        audioConnection: input.sdi!.audioConnection,
                                        profile: input.sdi!.profile,
                                        videoConnection: input.sdi!.videoConnection,
                                        videoFormat: input.sdi!.videoFormat,
                                        videoMode: input.sdi!.videoMode,
                                    }}
                                />
                            </Grid>
                        </>
                    )}
                </>
            )}
            {organization.channelSettings.theoads.enabled === true && (
                <>
                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12}>
                        <SectionTitle>THEOads integration</SectionTitle>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <FormControl>
                            <SwitchWithTitle
                                checked={input.theoads.enabled}
                                onChange={() =>
                                    onChangeTHEOads(
                                        !input.theoads.enabled,
                                        input.theoads.assetKeys,
                                        input.theoads.podDuration,
                                    )
                                }
                                title="Enable THEOads for this channel"
                            />
                        </FormControl>
                    </Grid>

                    {input.theoads.enabled === true && (
                        <>
                            <Grid item xs={12} md={7}>
                                <FormGroup>
                                    <HESPFormLabel label="Pod duration" />
                                    <TextField
                                        fullWidth
                                        type="number"
                                        size="small"
                                        onChange={(e) =>
                                            onChangeTHEOads(
                                                input.theoads.enabled,
                                                input.theoads.assetKeys,
                                                parseInt(e.target.value),
                                            )
                                        }
                                        variant="outlined"
                                        value={input.theoads.podDuration}
                                        InputProps={{
                                            inputProps: { min: 0, max: 1_000, step: 1 },
                                            style: {
                                                minWidth: "80px",
                                                fontSize: "14px",
                                                padding: "0px 10px",
                                                height: "43px",
                                            },
                                        }}
                                    />
                                </FormGroup>
                            </Grid>
                            <Grid item xs={12}>
                                <Wrapper>
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <THEOadsAssetKeys
                                                assetKeys={input.theoads.assetKeys}
                                                onChangeKeys={(assetKeys: THEOadsAssetKey[]) =>
                                                    onChangeTHEOads(
                                                        input.theoads.enabled,
                                                        assetKeys,
                                                        input.theoads.podDuration,
                                                    )
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </Wrapper>
                            </Grid>
                        </>
                    )}
                </>
            )}
        </Grid>
    );
}
